import classNames from 'classnames';
import React, { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Logo from '@/components/atoms/logo/logo';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import useMutationUpdateUserProfile from '@/integrations/content/users/update-user-profile/update-user-profile.hooks';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import styles from './profile-become-member-card.module.scss';

export interface ProfileBecomeMemberCardProps {
  /** The become loyalty modal content to show */
  becomeLoyaltyMemberModal?:
    | Content.Components['BecomeLoyaltyMemberModalDto']
    | null;
  /** The class name to apply */
  className?: string;
  /** Whether the card is loading */
  isLoading?: boolean;
}

const ProfileBecomeMemberCard = ({
  becomeLoyaltyMemberModal,
  className,
  isLoading,
}: ProfileBecomeMemberCardProps) => {
  const { setActiveModal } = usePageDetailsContext();
  const [isUpdating, setIsUpdating] = useState(false);

  const { mutateAsync: mutateAsyncUpdateUserProfile } =
    useMutationUpdateUserProfile();

  const handleRefreshPage = () => {
    if (!window) return;

    window.location.reload();
  };

  const handleBecomeMember = async () => {
    if (
      becomeLoyaltyMemberModal &&
      becomeLoyaltyMemberModal.modalType &&
      !becomeLoyaltyMemberModal.isLoggedIn
    ) {
      setActiveModal(becomeLoyaltyMemberModal.modalType);

      return;
    }

    try {
      setIsUpdating(true);

      await mutateAsyncUpdateUserProfile({
        data: {
          hasLoyaltyConsent: true,
        },
        refreshSession: true,
      });

      handleRefreshPage();
    } catch (error) {
      setIsUpdating(false);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className={classNames(styles.component, styles.isLoading)}>
        <div className={styles.topSection}>
          <SkeletonLoader
            className={classNames(styles.logo, styles.loader)}
            height="22px"
            width="50%"
          />

          <SkeletonLoader
            className={classNames(styles.cardHeadline, styles.loader)}
            height="22px"
            width="100%"
          />

          <SkeletonLoader
            className={classNames(styles.description, styles.loader)}
            height="22px"
            width="100%"
          />
        </div>

        <div className={styles.middleSection}>
          <SkeletonLoader
            className={classNames(styles.benefitsHeadline, styles.loader)}
            height="24px"
            width="40%"
          />

          <div className={styles.benefits}>
            {Array.from({ length: 3 }).map((_, benefitIndex) => (
              <div key={benefitIndex} className={styles.benefit}>
                <div className={styles.iconWrapper}>
                  <SkeletonLoader
                    borderRadius="999px"
                    className={classNames(styles.icon, styles.loader)}
                    height="20px"
                    width="20px"
                  />
                </div>

                <SkeletonLoader
                  className={classNames(styles.benefitText, styles.loader)}
                  height="16px"
                  width="60%"
                />
              </div>
            ))}
          </div>
        </div>

        <div className={styles.bottomSection}>
          <SkeletonLoader borderRadius="999px" height="48px" width="100%" />

          <SkeletonLoader
            className={classNames(styles.informationText, styles.loader)}
            height="12px"
            width="100%"
          />

          <SkeletonLoader
            className={classNames(styles.informationText, styles.loader)}
            height="12px"
            width="100%"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.topSection}>
        <Logo className={styles.logo} name="Travelheartfamily" />

        {becomeLoyaltyMemberModal?.headline && (
          <Typography className={styles.cardHeadline} tagStyle="headingMedium">
            {becomeLoyaltyMemberModal.headline}
          </Typography>
        )}

        {becomeLoyaltyMemberModal?.description && (
          <RichText
            className={styles.description}
            text={becomeLoyaltyMemberModal.description}
          />
        )}
      </div>

      {becomeLoyaltyMemberModal?.benefits &&
        becomeLoyaltyMemberModal.benefits.length > 0 && (
          <div className={styles.middleSection}>
            {becomeLoyaltyMemberModal.benefitsHeadline && (
              <Typography
                className={styles.benefitsHeadline}
                tagStyle="subHeading"
              >
                {becomeLoyaltyMemberModal.benefitsHeadline}
              </Typography>
            )}

            <div className={styles.benefits}>
              {becomeLoyaltyMemberModal.benefits.map(
                (benefit, benefitIndex) => (
                  <div key={benefitIndex} className={styles.benefit}>
                    <div className={styles.iconWrapper}>
                      <Icon
                        className={styles.icon}
                        name={benefit.icon}
                        size={16}
                      />
                    </div>

                    <Typography
                      className={styles.benefitText}
                      tagStyle="bodyMedium"
                    >
                      {benefit.headline}
                    </Typography>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

      <div className={styles.bottomSection}>
        <Button
          icon="Travelheart"
          iconPosition="left"
          iconSize={16}
          isLoading={isUpdating}
          onClick={() => handleBecomeMember()}
          text={becomeLoyaltyMemberModal?.buttonText}
          variant="primary"
        />

        {becomeLoyaltyMemberModal?.informationText && (
          <RichText
            className={styles.informationText}
            text={becomeLoyaltyMemberModal.informationText}
          />
        )}
      </div>
    </div>
  );
};

ProfileBecomeMemberCard.displayName = 'ProfileBecomeMemberCard';

export default ProfileBecomeMemberCard;
